import { useEffect, useState } from "react";
import { AlertTriangle, ListTree } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { cleanLayout, inferLayout, screenSnap, screensFor, UNIT_LABEL, useLines } from "@/lib/components";
import { money, serializeItem } from "@/lib/pricing";

const sel = "h-6 max-w-[190px] rounded border border-slate-800 bg-slate-900/70 px-1 text-[11px] text-slate-100 outline-none focus:border-amber-500/70";
const lab = "flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500";

function Extras({ row, line, idx, onPatch }) {
  const chosen = Object.fromEntries((row.extras || []).map((e) => [e.name, e.qty]));
  const setQty = (name, qty) => onPatch({ extras: [...(row.extras || []).filter((e) => e.name !== name), ...(Number(qty) > 0 ? [{ name, qty: Number(qty) }] : [])] });
  return (
    <Popover>
      <PopoverTrigger data-testid={`quote-item-extras-button-${idx}`} className="text-[11px] font-semibold text-sky-300 hover:underline">
        Extras{row.extras?.length ? ` (${row.extras.length})` : ""}
      </PopoverTrigger>
      <PopoverContent className="z-[60] w-80 border-slate-800 bg-slate-950 p-3 opacity-100 shadow-2xl" data-testid={`quote-item-extras-popover-${idx}`}>
        <p className="mb-2 text-xs font-semibold text-slate-300">Extras for {line.name}</p>
        {line.extras.length === 0 && <p className="text-xs text-slate-500">No extras on this product line.</p>}
        {line.extras.map((e, k) => (
          <label key={e.name} className="flex items-center justify-between gap-2 py-1 text-xs text-slate-300">
            <span>{e.name} <span className="text-slate-500">· {UNIT_LABEL[e.unit]}</span></span>
            <input data-testid={`quote-item-extra-qty-${idx}-${k}`} type="number" min={0} step="1" value={chosen[e.name] ?? ""} placeholder="0"
              onChange={(ev) => setQty(e.name, ev.target.value)} className="h-6 w-14 rounded border border-slate-800 bg-slate-900 px-1 text-right font-mono-num" />
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function Breakdown({ row, idx }) {
  const parts = row.calc?.parts || row.component?.breakdown || [];
  return (
    <Popover>
      <PopoverTrigger data-testid={`quote-item-breakdown-button-${idx}`} title="Cost breakdown" className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-300 hover:underline">
        <ListTree className="h-3 w-3" /> Breakdown
      </PopoverTrigger>
      <PopoverContent className="z-[60] w-96 border-slate-800 bg-slate-950 p-3 opacity-100 shadow-2xl data-[state=open]:animate-none" data-testid={`quote-item-breakdown-${idx}`}>
        <p className="mb-2 text-xs font-semibold text-slate-300">Cost per unit (before markup)</p>
        {parts.map((p, k) => {
          const over = /\(oversized/.test(p.label), info = p.amount === 0 && /^(Sections|.*· Sections)/.test(p.label);
          return (
            <div key={k} data-testid={`quote-item-breakdown-row-${idx}-${k}`} className={`flex justify-between gap-3 border-b border-slate-800/60 py-1 text-xs ${over ? "font-semibold text-[#D70015]" : "text-slate-400"}`}>
              <span>{p.label}</span><span className="font-mono-num text-slate-200">{info ? "" : money(p.amount)}</span>
            </div>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

function useStaffPrice(row, onPatch, staff) {
  const { quoteId } = useLines();
  const key = JSON.stringify([row.product_line_id, row.width_in, row.height_in, row.layout, row.track, row.glass_upgrade, row.extras, row.config, row.panel_widths, row.row_heights, row.sash_sizes, row.shape_id, row.screen_id, row.screen_pull, row.series]);
  useEffect(() => {
    if (!staff || !row.product_line_id || row.design?.root) return undefined;
    const t = setTimeout(() => {
      api.post(`/product-lines/price${quoteId ? `?quote_id=${quoteId}` : ""}`, serializeItem(row))
        .then((r) => onPatch({ component_unit_sell: r.data.unit_sell, warning: r.data.warning })).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, staff]);
}

const appliesTo = (s, line) => s.applies_all || s.line_ids.includes(line.id) || (!!line.type_id && s.type_ids.includes(line.type_id));

function ShapeSelect({ row, line, idx, onPatch }) {
  const { shapes = [], shapeRates = {} } = useLines();
  const mine = shapes.filter((s) => appliesTo(s, line) || s.id === row.shape_id);
  const gone = row.shape_id && !shapes.some((s) => s.id === row.shape_id);
  if (!mine.length && !gone) return null;
  const pick = (id) => {
    const s = shapes.find((x) => x.id === id);
    const component = { ...(row.component || {}) };
    delete component.shape;
    if (s && shapeRates[id]) component.shape = shapeRates[id];
    onPatch({ shape_id: s ? id : null, shape_name: s?.name || "", shape_style: s?.style || "", component });
  };
  return (
    <select data-testid={`quote-item-shape-select-${idx}`} value={row.shape_id || ""} onChange={(e) => pick(e.target.value)} className={sel} title="Arch / custom shape">
      <option value="">No shape</option>
      {gone && <option value={row.shape_id}>{row.shape_name || "Shape"} (deleted)</option>}
      {mine.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
    </select>
  );
}

function ScreenSelect({ row, line, idx, onPatch }) {
  const { choices, rates } = useLines();
  if (line.includes_screen) return <span data-testid={`quote-item-screen-included-${idx}`} className="rounded bg-emerald-700/40 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-200">Screen included</span>;
  const mine = screensFor(choices, line);
  if (!mine.length) return null;
  const cur = mine.find((s) => s.id === row.screen_id);
  const pick = (id) => {
    const s = mine.find((x) => x.id === id);
    const component = { ...(row.component || {}) };
    delete component.screen;
    if (s && rates[id]) component.screen = screenSnap(rates[id]);
    onPatch({ screen_id: s ? id : null, screen_name: s?.name || "", screen_pull: s?.pull === "double" ? "double" : "single", component });
  };
  return (
    <>
      <select data-testid={`quote-item-screen-select-${idx}`} value={cur ? row.screen_id : ""} onChange={(e) => pick(e.target.value)} className={sel} title="Screen">
        <option value="">No screen</option>
        {mine.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      {cur?.pull === "both" && (
        <select data-testid={`quote-item-screen-pull-select-${idx}`} value={row.screen_pull || "single"} onChange={(e) => onPatch({ screen_pull: e.target.value })} className={sel}>
          <option value="single">Single-pull</option>
          <option value="double">Double-pull</option>
        </select>
      )}
    </>
  );
}

export function ComponentFields({ row, idx, onPatch, staff }) {
  const { choices: all, rates, supplierOnly } = useLines();
  const choices = all.filter((c) => c.category !== "screen");
  const [layout, setLayout] = useState(row.layout || "");
  useStaffPrice(row, onPatch, staff);
  const mine = choices.filter((c) => c.type_id === row.type_id || c.id === row.product_line_id);
  const others = supplierOnly ? choices.filter((c) => !mine.includes(c)) : [];
  if (!mine.length && !supplierOnly) return null;
  const line = choices.find((c) => c.id === row.product_line_id);
  const pick = (id) => {
    if (!id) return onPatch({ product_line_id: null, no_line: true, pricing_mode: "sqft", component: {}, extras: [], track: "", glass_upgrade: "", layout: "", screen_id: null, screen_name: "" });
    const c = choices.find((x) => x.id === id);
    const lay = c.kind === "slider" ? "" : inferLayout(c.category, c.default_layout, [row.raw_type, row.product_name, row.model, row.type_name].join(" "));
    setLayout(lay);
    const keep = screensFor(all, c).some((s) => s.id === row.screen_id);
    const scr = keep && row.component?.screen ? { screen: row.component.screen } : {};
    onPatch({ product_line_id: id, no_line: false, pricing_mode: "component", component: rates[id] ? { line: rates[id], ...scr } : {}, component_unit_sell: null,
      extras: (c.extras || []).filter((e) => e.default_on).map((e) => ({ name: e.name, qty: 1 })), track: "", glass_upgrade: "",
      shape_id: null, shape_name: "", shape_style: "", ...(keep ? {} : { screen_id: null, screen_name: "" }),
      layout: lay, layout_auto: true, config: c.kind === "slider" ? row.config || c.default_layout.replace(/\|/g, "") : row.config });
  };
  return (
    <div className="mx-2 mt-1 space-y-1" data-testid={`quote-item-component-${idx}`}>
      <select data-testid={`quote-item-product-line-select-${idx}`} value={row.product_line_id || ""} onChange={(e) => pick(e.target.value)} className={`${sel} ${staff && row.catalog_id ? "hidden" : ""}`}>
        {supplierOnly ? <option value="" disabled>Pick a supplier product line</option> : <option value="">Type pricing (no product line)</option>}
        {others.length ? (
          <>
            {mine.length > 0 && <optgroup label="For this type">{mine.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</optgroup>}
            <optgroup label="All product lines">{others.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</optgroup>
          </>
        ) : mine.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      {supplierOnly && !row.product_line_id && row.price_source !== "estimate" && (row._legacy ? (
        <span data-testid={`quote-item-legacy-price-${idx}`} className="ml-1 text-[10px] font-semibold text-slate-500">Saved price · pick a product line to reprice</span>
      ) : (
        <span data-testid={`quote-item-no-supplier-price-${idx}`} className="ml-1 rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">No supplier price</span>
      ))}
      {line && (
        <div className="flex flex-wrap items-center gap-2">
          {line.kind !== "slider" && (
            <label className={lab}>Panels
              <input data-testid={`quote-item-layout-input-${idx}`} value={layout} placeholder="O|X or X/O" maxLength={60}
                title="O = fixed, X = opening sash, left to right from outside" onChange={(e) => setLayout(e.target.value.toUpperCase())}
                onBlur={() => { const v = cleanLayout(layout, line.default_layout); setLayout(v); if (v !== row.layout) onPatch({ layout: v, layout_auto: false }); }}
                className="h-6 w-28 rounded border border-slate-800 bg-slate-900/70 px-1.5 font-mono-num text-xs tracking-widest text-slate-100 outline-none focus:border-amber-500/70" />
            </label>
          )}
          {line.kind !== "slider" && /[/(]/.test(row.layout || "") && (
            <label className={lab}>Row heights
              <input key={`${row.layout}:${(row.row_heights || []).join(",")}`} data-testid={`quote-item-row-heights-input-${idx}`} defaultValue={(row.row_heights || []).join(", ")} placeholder="equal"
                title="Top-to-bottom row heights in inches (must add up to the height); empty = equal"
                onBlur={(e) => onPatch({ row_heights: e.target.value.split(/[ ,]+/).map(Number).filter((x) => x > 0) })}
                className="h-6 w-24 rounded border border-slate-800 bg-slate-900/70 px-1.5 font-mono-num text-xs text-slate-100 outline-none focus:border-amber-500/70" />
            </label>
          )}
          {line.kind !== "slider" && /X/.test(row.layout || line.default_layout || "") && (
            <label className={lab}>Sash W×H
              <input key={(row.sash_sizes || []).map((s) => s.join("x")).join(",")} data-testid={`quote-item-sash-sizes-input-${idx}`}
                defaultValue={(row.sash_sizes || []).map((s) => s.join("x")).join(", ")} placeholder="equal split"
                title="Each opening sash width × height in inches, left to right, e.g. 24x48, 24x48. Empty = equal split of the layout"
                onBlur={(e) => onPatch({ sash_sizes: e.target.value.split(/[,;]+/).map((p) => p.toLowerCase().split(/[x×*]/).map(Number)).filter((s) => s.length === 2 && s[0] > 0 && s[1] > 0) })}
                className="h-6 w-32 rounded border border-slate-800 bg-slate-900/70 px-1.5 font-mono-num text-xs text-slate-100 outline-none focus:border-amber-500/70" />
            </label>
          )}
          {line.kind === "slider" && line.tracks.length > 0 && (
            <select data-testid={`quote-item-track-select-${idx}`} value={row.track || line.tracks[0]} onChange={(e) => onPatch({ track: e.target.value })} className={sel}>
              {line.tracks.map((t) => <option key={t}>{t}</option>)}
            </select>
          )}
          {line.glass_upgrades.length > 0 && (
            <select data-testid={`quote-item-glass-upgrade-select-${idx}`} value={row.glass_upgrade || ""} onChange={(e) => onPatch({ glass_upgrade: e.target.value })} className={sel}>
              <option value="">Standard glass</option>
              {line.glass_upgrades.map((u) => <option key={u}>{u}</option>)}
            </select>
          )}
          <ShapeSelect row={row} line={line} idx={idx} onPatch={onPatch} />
          <ScreenSelect row={row} line={line} idx={idx} onPatch={onPatch} />
          <Extras row={row} line={line} idx={idx} onPatch={onPatch} />
          {!staff && <Breakdown row={row} idx={idx} />}
        </div>
      )}
      {(row.calc?.warn || row.warning) && (
        <p data-testid={`quote-item-oversize-warning-${idx}`} className="flex items-center gap-1 text-[10px] font-semibold text-orange-300">
          <AlertTriangle className="h-3 w-3" /> {row.calc?.warn || row.warning}
        </p>
      )}
    </div>
  );
}
